import { useState, useEffect, useRef, useCallback } from "react";
import { useAuth } from "@/lib/auth";
import API from "@/lib/api";

const ACTIVITY_EVENTS = ["mousedown", "mousemove", "keydown", "scroll", "touchstart", "click"];
const WARNING_SECONDS = 60; // Show warning 60s before logout
const AUTO_SAVE_EVENT = "mep-auto-save"; // Custom event name for auto-save

export function useIdleTimeout() {
  const { user, logout } = useAuth();
  const [timeoutMinutes, setTimeoutMinutes] = useState(15);
  const [autoSaveEnabled, setAutoSaveEnabled] = useState(true);
  const [autoSaveInterval, setAutoSaveInterval] = useState(5);
  const [secondsLeft, setSecondsLeft] = useState(null); // null = not warning
  const [showWarning, setShowWarning] = useState(false);
  const timerRef = useRef(null);
  const warningTimerRef = useRef(null);
  const countdownRef = useRef(null);
  const autoSaveTimerRef = useRef(null);
  const lastActivityRef = useRef(Date.now());

  // Fetch security settings from session security config
  useEffect(() => {
    if (!user) return;
    API.get("/settings/session-security").then(r => {
      const s = r.data || {};
      if (s.idle_timeout_minutes) setTimeoutMinutes(s.idle_timeout_minutes);
      if (typeof s.auto_save_enabled === "boolean") setAutoSaveEnabled(s.auto_save_enabled);
      if (s.auto_save_interval_minutes) setAutoSaveInterval(s.auto_save_interval_minutes);
    }).catch(() => {});
  }, [user]);

  const resetTimers = useCallback(() => {
    lastActivityRef.current = Date.now();
    setShowWarning(false);
    setSecondsLeft(null);

    // Clear existing timers
    if (timerRef.current) clearTimeout(timerRef.current);
    if (warningTimerRef.current) clearTimeout(warningTimerRef.current);
    if (countdownRef.current) clearInterval(countdownRef.current);

    if (!user || timeoutMinutes <= 0) return;

    const timeoutMs = timeoutMinutes * 60 * 1000;
    const warningMs = timeoutMs - (WARNING_SECONDS * 1000);

    // Set warning timer
    if (warningMs > 0) {
      warningTimerRef.current = setTimeout(() => {
        setShowWarning(true);
        setSecondsLeft(WARNING_SECONDS);
        // Start countdown
        countdownRef.current = setInterval(() => {
          setSecondsLeft(prev => {
            if (prev <= 1) {
              clearInterval(countdownRef.current);
              return 0;
            }
            return prev - 1;
          });
        }, 1000);
      }, warningMs);
    }

    // Set logout timer
    timerRef.current = setTimeout(() => {
      setShowWarning(false);
      logout();
    }, timeoutMs);
  }, [user, timeoutMinutes, logout]);

  // Attach activity listeners
  useEffect(() => {
    if (!user) return;

    const handleActivity = () => {
      // Only reset if not in warning state (user must click "Stay Logged In" to dismiss warning)
      if (!showWarning) {
        resetTimers();
      }
    };

    ACTIVITY_EVENTS.forEach(evt => window.addEventListener(evt, handleActivity, { passive: true }));
    resetTimers();

    return () => {
      ACTIVITY_EVENTS.forEach(evt => window.removeEventListener(evt, handleActivity));
      if (timerRef.current) clearTimeout(timerRef.current);
      if (warningTimerRef.current) clearTimeout(warningTimerRef.current);
      if (countdownRef.current) clearInterval(countdownRef.current);
    };
  }, [user, resetTimers, showWarning]);

  // Handle visibility change (tab switch, phone lock)
  useEffect(() => {
    if (!user) return;
    const handleVisibility = () => {
      if (document.visibilityState === "visible") {
        const elapsed = Date.now() - lastActivityRef.current;
        const timeoutMs = timeoutMinutes * 60 * 1000;
        if (elapsed >= timeoutMs) {
          logout();
        } else if (elapsed >= timeoutMs - WARNING_SECONDS * 1000) {
          const remaining = Math.ceil((timeoutMs - elapsed) / 1000);
          setShowWarning(true);
          setSecondsLeft(remaining);
          if (countdownRef.current) clearInterval(countdownRef.current);
          countdownRef.current = setInterval(() => {
            setSecondsLeft(prev => {
              if (prev <= 1) {
                clearInterval(countdownRef.current);
                logout();
                return 0;
              }
              return prev - 1;
            });
          }, 1000);
        }
      }
    };
    document.addEventListener("visibilitychange", handleVisibility);
    return () => document.removeEventListener("visibilitychange", handleVisibility);
  }, [user, timeoutMinutes, logout]);

  // Auto-save timer — dispatches custom event every N minutes
  useEffect(() => {
    if (!user || !autoSaveEnabled || autoSaveInterval <= 0) return;
    autoSaveTimerRef.current = setInterval(() => {
      window.dispatchEvent(new CustomEvent(AUTO_SAVE_EVENT));
    }, autoSaveInterval * 60 * 1000);
    return () => { if (autoSaveTimerRef.current) clearInterval(autoSaveTimerRef.current); };
  }, [user, autoSaveEnabled, autoSaveInterval]);

  const stayLoggedIn = useCallback(() => {
    setShowWarning(false);
    setSecondsLeft(null);
    if (countdownRef.current) clearInterval(countdownRef.current);
    resetTimers();
  }, [resetTimers]);

  return {
    showWarning,
    secondsLeft,
    stayLoggedIn,
    timeoutMinutes,
    autoSaveEnabled,
    autoSaveInterval,
  };
}

/**
 * Hook for components to react to the global auto-save event.
 * Usage: useAutoSave(() => { saveMyData(); });
 */
export function useAutoSave(callback) {
  const callbackRef = useRef(callback);
  callbackRef.current = callback;

  useEffect(() => {
    const handler = () => callbackRef.current();
    window.addEventListener(AUTO_SAVE_EVENT, handler);
    return () => window.removeEventListener(AUTO_SAVE_EVENT, handler);
  }, []);
}
